import { useState } from "react";
import PropTypes from "prop-types";

import {
  StyledDropdown,
  StyledToggle,
  StyledMenu,
  ScrollArea,
  Row,
  EmptyState,
  FooterBar,
  TriggerIconWrap,
  TriggerLabel,
  Chevron,
  OptionIconWrap,
  OptionLabelColumn,
  OptionLabel,
  OptionDescription,
  CheckMark,
  CheckIcon,
} from "./Dropdown.styles";

// Decorative icon — label is the accessible name, so alt="" is intentional.
const renderIcon = (icon) => {
  if (!icon) return null;
  if (typeof icon === "string") return <img src={icon} alt="" />;
  return icon;
};

export const Dropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select",
  // block: stretches trigger + menu to full width
  block = false,
  minWidth,
  disabled = false,
  emptyText = "No results found",
  loadingText,
  errorText,
  footer,
}) => {
  const [show, setShow] = useState(false);

  const selected = options.find((o) => o.value === value);
  const triggerLabel = selected?.label ?? placeholder;
  const triggerIcon = selected?.icon ?? null;

  // Status priority: loading > error > empty → options.
  const statusMessage =
    loadingText || errorText || (options.length === 0 ? emptyText : null);

  const handleSelect = (itemValue) => {
    onChange(itemValue);
    setShow(false);
  };

  return (
    <StyledDropdown show={show} onToggle={setShow} $block={block}>
      {/* variant="light" keeps bootstrap's .btn-light baseline (white bg + dark text). */}
      <StyledToggle disabled={disabled} variant="light" $block={block}>
        {triggerIcon && (
          <TriggerIconWrap>{renderIcon(triggerIcon)}</TriggerIconWrap>
        )}
        <TriggerLabel>{triggerLabel}</TriggerLabel>
        <Chevron />
      </StyledToggle>

      <StyledMenu $block={block} $minWidth={minWidth}>
        <ScrollArea>
          {statusMessage ? (
            <EmptyState>{statusMessage}</EmptyState>
          ) : (
            options.map((option) => {
              const isSelected = value === option.value;
              return (
                <Row
                  key={option.value}
                  $selected={isSelected}
                  disabled={option.disabled}
                  title={option.title}
                  onClick={() => handleSelect(option.value)}
                  aria-selected={isSelected}
                >
                  {option.icon && (
                    <OptionIconWrap>{renderIcon(option.icon)}</OptionIconWrap>
                  )}
                  <OptionLabelColumn>
                    <OptionLabel>{option.label}</OptionLabel>
                    {option.description && (
                      <OptionDescription>
                        {option.description}
                      </OptionDescription>
                    )}
                  </OptionLabelColumn>
                  {isSelected && (
                    <CheckMark>
                      <CheckIcon />
                    </CheckMark>
                  )}
                </Row>
              );
            })
          )}
        </ScrollArea>
        {/* Footer closes menu on any click inside (e.g. "View all" button). */}
        {footer && (
          <FooterBar onClick={() => setShow(false)}>{footer}</FooterBar>
        )}
      </StyledMenu>
    </StyledDropdown>
  );
};

Dropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      description: PropTypes.node,
      title: PropTypes.string,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  block: PropTypes.bool,
  minWidth: PropTypes.string,
  disabled: PropTypes.bool,
  emptyText: PropTypes.string,
  loadingText: PropTypes.string,
  errorText: PropTypes.string,
  footer: PropTypes.node,
};

Dropdown.displayName = "Dropdown";
